import React, { useState, useEffect, useCallback } from 'react';
import { 
  Phone, Calendar, X, Rocket, ArrowRight, 
  MessageCircle, Send, User, Building2, Tag,
  CheckCircle, Sparkles
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

/**
 * SmartConversionLayer
 * High-converting floating CTA system for desktop and mobile
 * Features:
 * - Desktop: Floating bottom-right card
 * - Mobile: Sticky bottom bar + slide-up
 * - Smart triggers (scroll, time, tool interaction)
 * - Mini form → WhatsApp/Call flow
 * - ROI-based personalized messaging
 */
const SmartConversionLayer = () => {
  // Visibility states
  const [isVisible, setIsVisible] = useState(false);
  const [showMobileSlideUp, setShowMobileSlideUp] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [hasConverted, setHasConverted] = useState(false);
  
  // Form states
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    brand: '',
    category: 'Fashion'
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  
  // Personalization state (from calculator interaction)
  const [userIntent, setUserIntent] = useState({
    hasUsedTool: false,
    roi: null,
    source: null
  });

  // Check if mobile
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Smart trigger logic
  useEffect(() => {
    if (isDismissed || hasConverted) return;
    
    // Check session storage for dismissal
    const dismissed = sessionStorage.getItem('conversion_dismissed');
    const converted = sessionStorage.getItem('conversion_converted');
    if (dismissed) { setIsDismissed(true); return; }
    if (converted) { setHasConverted(true); return; }

    let timeoutId;
    let hasTriggered = false;

    const triggerVisibility = (source) => {
      if (hasTriggered || isDismissed) return;
      hasTriggered = true;
      setIsVisible(true);
      setUserIntent(prev => ({ ...prev, source }));
      
      // Track event
      if (typeof gtag !== 'undefined') {
        window.gtag('event', 'conversion_layer_shown', { source });
      }
    };

    // Trigger 1: After 30 seconds
    timeoutId = setTimeout(() => triggerVisibility('time'), 30000);

    // Trigger 2: After 50% scroll
    const handleScroll = () => {
      const scrollPercent = (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100;
      if (scrollPercent > 50) {
        triggerVisibility('scroll');
      }
      // High-intent trigger for mobile slide-up (80% scroll)
      if (scrollPercent > 80 && isMobile) {
        setShowMobileSlideUp(true);
      }
    };
    window.addEventListener('scroll', handleScroll);

    // Trigger 3: Tool interaction (listen for custom event)
    const handleToolInteraction = (e) => {
      triggerVisibility('tool');
      if (e.detail?.roi) {
        setUserIntent(prev => ({ 
          ...prev, 
          hasUsedTool: true, 
          roi: e.detail.roi 
        }));
      }
      // Show mobile slide-up for tool users
      if (isMobile) {
        setTimeout(() => setShowMobileSlideUp(true), 2000);
      }
    };
    window.addEventListener('calculatorUsed', handleToolInteraction);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('calculatorUsed', handleToolInteraction);
    };
  }, [isDismissed, hasConverted, isMobile]);

  // Dismiss handler
  const handleDismiss = useCallback(() => {
    setIsVisible(false);
    setShowMobileSlideUp(false);
    setIsDismissed(true);
    sessionStorage.setItem('conversion_dismissed', 'true');
  }, []);

  // Get personalized message based on ROI
  const getPersonalizedMessage = () => {
    if (userIntent.roi !== null) {
      if (userIntent.roi >= 25) {
        return { title: "You're ready to scale!", subtitle: "Your margins look great. Let's accelerate your growth." };
      } else if (userIntent.roi >= 10) {
        return { title: "Good potential detected", subtitle: "A few optimizations and you'll be ready to scale." };
      } else {
        return { title: "Let's fix your margins first", subtitle: "Our experts can help optimize your business model." };
      }
    }
    return { title: "Ready to scale your brand?", subtitle: "Get instant expert guidance for marketplace onboarding" };
  };

  const message = getPersonalizedMessage();

  // Form handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = (action) => {
    if (!formData.name.trim() || !formData.brand.trim()) return;
    
    setFormSubmitted(true);
    setHasConverted(true);
    sessionStorage.setItem('conversion_converted', 'true');

    // Track event
    if (typeof gtag !== 'undefined') {
      window.gtag('event', 'conversion_form_submit', {
        action,
        category: formData.category,
        source: userIntent.source
      });
    }

    // Store lead data (will be sent via existing lead capture)
    const leadData = {
      ...formData,
      source: `Conversion Layer - ${userIntent.source}`,
      roi: userIntent.roi,
      timestamp: new Date().toISOString()
    };

    // Send to backend (async, non-blocking)
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads/quick`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(leadData)
    }).catch(() => {});

    if (action === 'call') {
      // Track call click
      if (typeof gtag !== 'undefined') {
        window.gtag('event', 'call_click', { source: 'conversion_layer' });
      }
      // On mobile, trigger call directly
      if (isMobile) {
        window.location.href = 'tel:+917743005242';
      }
    } else if (action === 'whatsapp') {
      // Track WhatsApp click
      if (typeof gtag !== 'undefined') {
        window.gtag('event', 'whatsapp_click', { source: 'conversion_layer' });
      }
      // Open WhatsApp with pre-filled message
      const whatsappMessage = encodeURIComponent(
        `Hi, I want to scale my brand on marketplaces.\n\nName: ${formData.name}\nBrand: ${formData.brand}\nCategory: ${formData.category}`
      );
      window.open(`https://wa.me/917743005242?text=${whatsappMessage}`, '_blank');
    }
  };

  const handleCallClick = () => {
    if (typeof gtag !== 'undefined') {
      window.gtag('event', 'call_click', { source: 'conversion_layer_direct' });
    }
    setShowForm(true);
  };

  const handleBookDemo = () => {
    if (typeof gtag !== 'undefined') {
      window.gtag('event', 'book_demo_click', { source: 'conversion_layer' });
    }
    // Dispatch demo modal event
    const event = new CustomEvent('openDemoModal');
    window.dispatchEvent(event);
    handleDismiss();
  };

  // Don't render if dismissed or converted
  if (isDismissed || hasConverted || !isVisible) return null;

  // Categories for form
  const categories = ['Fashion', 'Beauty', 'Lifestyle', 'Electronics', 'Food & FMCG', 'Pet Products', 'Other'];

  return (
    <>
      {/* ==================== DESKTOP FLOATING CARD ==================== */}
      {!isMobile && (
        <div 
          className="fixed bottom-24 right-6 z-40 animate-in slide-in-from-bottom-5 duration-500"
          data-testid="conversion-desktop-card"
        >
          {!showForm ? (
            // Main Card
            <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 p-5 w-[320px] relative overflow-hidden">
              {/* Gradient accent */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#ef4444] via-orange-500 to-amber-500"></div>
              
              {/* Close button */}
              <button 
                onClick={handleDismiss}
                className="absolute top-3 right-3 text-slate-400 hover:text-slate-600 transition-colors"
                aria-label="Dismiss"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Content */}
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-[#ef4444] to-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Rocket className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-800 text-lg leading-tight">{message.title}</h3>
                  <p className="text-slate-500 text-sm mt-1">{message.subtitle}</p>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-3">
                <Button 
                  onClick={handleCallClick}
                  className="flex-1 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-xl py-5 font-semibold shadow-lg shadow-green-500/25"
                  data-testid="conversion-call-btn"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Call Now
                </Button>
                <Button 
                  onClick={handleBookDemo}
                  variant="outline"
                  className="flex-1 border-2 border-slate-200 text-slate-700 hover:border-[#ef4444] hover:text-[#ef4444] rounded-xl py-5 font-semibold"
                  data-testid="conversion-demo-btn"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Book Demo
                </Button>
              </div>

              {/* Trust indicator */}
              <p className="text-center text-xs text-slate-400 mt-3">
                Trusted by 4200+ brands across India
              </p>
            </div>
          ) : (
            // Form Card
            <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 p-5 w-[320px] relative overflow-hidden animate-in fade-in duration-300">
              {/* Gradient accent */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-green-500 to-emerald-500"></div>
              
              {/* Close button */}
              <button 
                onClick={() => setShowForm(false)}
                className="absolute top-3 right-3 text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>

              {!formSubmitted ? (
                <>
                  <h3 className="font-bold text-slate-800 text-lg mb-4">Quick Details</h3>
                  
                  <div className="space-y-3">
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        type="text"
                        name="name"
                        placeholder="Your Name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="pl-10"
                        data-testid="conversion-form-name"
                      />
                    </div>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        type="text"
                        name="brand"
                        placeholder="Brand Name"
                        value={formData.brand}
                        onChange={handleInputChange}
                        className="pl-10"
                        data-testid="conversion-form-brand"
                      />
                    </div>
                    <div className="relative">
                      <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <select
                        name="category"
                        value={formData.category}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                        data-testid="conversion-form-category"
                      >
                        {categories.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="flex gap-3 mt-4">
                    <Button 
                      onClick={() => handleFormSubmit('call')}
                      disabled={!formData.name.trim() || !formData.brand.trim()}
                      className="flex-1 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-xl py-4 font-semibold disabled:opacity-50"
                    >
                      <Phone className="w-4 h-4 mr-2" />
                      Call
                    </Button>
                    <Button 
                      onClick={() => handleFormSubmit('whatsapp')}
                      disabled={!formData.name.trim() || !formData.brand.trim()}
                      className="flex-1 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-xl py-4 font-semibold disabled:opacity-50"
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      WhatsApp
                    </Button>
                  </div>
                </>
              ) : (
                // Success State
                <div className="text-center py-4">
                  <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <CheckCircle className="w-7 h-7 text-green-600" />
                  </div>
                  <h3 className="font-bold text-slate-800 text-lg mb-2">Details Saved!</h3>
                  <p className="text-slate-500 text-sm mb-4">Call us now for instant assistance</p>
                  <a 
                    href="tel:+917743005242"
                    className="inline-flex items-center gap-2 bg-green-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-green-600 transition-colors"
                  >
                    <Phone className="w-5 h-5" />
                    +91 77430 05242
                  </a>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ==================== MOBILE STICKY BOTTOM BAR ==================== */}
      {isMobile && (
        <>
          {/* Sticky Bottom Bar */}
          <div 
            className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-slate-200 shadow-2xl animate-in slide-in-from-bottom duration-500"
            data-testid="conversion-mobile-bar"
          >
            {!showForm ? (
              <div className="flex items-center gap-2 p-3 max-w-lg mx-auto">
                <Button 
                  onClick={handleCallClick}
                  className="flex-1 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl py-4 font-semibold shadow-lg"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Call Expert Now
                </Button>
                <Button 
                  onClick={handleBookDemo}
                  variant="outline"
                  className="flex-1 border-2 border-slate-200 text-slate-700 rounded-xl py-4 font-semibold"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Book Demo
                </Button>
                <button 
                  onClick={handleDismiss}
                  className="p-2 text-slate-400"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            ) : (
              // Mobile Form
              <div className="p-4 max-w-lg mx-auto">
                {!formSubmitted ? (
                  <>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-bold text-slate-800">Quick Details</h3>
                      <button onClick={() => setShowForm(false)} className="text-slate-400">
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    <div className="flex gap-2 mb-3">
                      <Input
                        type="text"
                        name="name"
                        placeholder="Name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="flex-1"
                      />
                      <Input
                        type="text"
                        name="brand"
                        placeholder="Brand"
                        value={formData.brand}
                        onChange={handleInputChange}
                        className="flex-1"
                      />
                    </div>
                    <div className="flex gap-2">
                      <select
                        name="category"
                        value={formData.category}
                        onChange={handleInputChange}
                        className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-slate-700 text-sm"
                      >
                        {categories.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                      <Button 
                        onClick={() => handleFormSubmit('call')}
                        disabled={!formData.name.trim() || !formData.brand.trim()}
                        className="bg-green-500 text-white rounded-xl px-4 disabled:opacity-50"
                      >
                        <Phone className="w-4 h-4 mr-1" />
                        Call
                      </Button>
                      <Button 
                        onClick={() => handleFormSubmit('whatsapp')}
                        disabled={!formData.name.trim() || !formData.brand.trim()}
                        className="bg-green-600 text-white rounded-xl px-4 disabled:opacity-50"
                      >
                        <MessageCircle className="w-4 h-4" />
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-2">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="font-semibold text-slate-800">Details Saved!</span>
                    </div>
                    <a 
                      href="tel:+917743005242"
                      className="inline-flex items-center gap-2 bg-green-500 text-white px-6 py-3 rounded-xl font-semibold"
                    >
                      <Phone className="w-5 h-5" />
                      Call +91 77430 05242
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Mobile Slide-Up (High Intent) */}
          {showMobileSlideUp && !showForm && (
            <div 
              className="fixed bottom-20 left-4 right-4 z-30 bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl p-4 shadow-2xl animate-in slide-in-from-bottom duration-500"
              data-testid="conversion-mobile-slideup"
            >
              <button 
                onClick={() => setShowMobileSlideUp(false)}
                className="absolute top-2 right-2 text-slate-400"
              >
                <X className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-[#ef4444] to-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-white text-sm">{message.title}</h3>
                  <p className="text-slate-400 text-xs">Talk to our expert instantly</p>
                </div>
                <a 
                  href="tel:+917743005242"
                  className="bg-green-500 text-white p-3 rounded-xl"
                  onClick={() => {
                    if (typeof gtag !== 'undefined') {
                      window.gtag('event', 'call_click', { source: 'mobile_slideup' });
                    }
                  }}
                >
                  <Phone className="w-5 h-5" />
                </a>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default SmartConversionLayer;
